import { isPlatformServer } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  PLATFORM_ID,
  QueryList,
  SimpleChange,
  SimpleChanges,
  TrackByFunction,
  ViewChildren,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, Observable } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';
import { ASPECT_RATIO_CONFIG, CarouselType } from '../../../common';
import { CustomerStatus } from '../../../domain';
import { IntersectionMarkerDirective } from '../../directives/intersection-marker/intersection-marker.directive';
import { AbstractDetectDeviceService, AbstractGatewayService, Screen, ScreenService } from '../../services';
import { DestroyableBase } from '../../utils';
import { CAROUSEL_ITEM_ASPECT_RATIO, CAROUSEL_ITEM_DISPLAY } from '../../utils/carousel-layouts';
import { CarouselItemVideoDetailsForGtmAnalytics } from '../carousel-item/carousel-item.component';
import { ContentViewModel } from '../carousel-item/content-view.model';
import { GenericCarouselItemViewModel } from '../generic-carousel-item/generic-carousel-item.view-model';
import { SeasonCarouselItemViewModel } from '../season-carousel-item/season-carousel-item.view-model';
import { CarouselViewModel, CarouselViewModelItem } from './carousel.view-model';

enum AnimationDirection {
  PREVIOUS = 'PREVIOUS',
  NEXT = 'NEXT',
}

const BASE_TRANSLATE_X = 100;

@Component({
  selector: 'osn-shared-carousel',
  templateUrl: './carousel.component.html',
  styleUrls: ['./carousel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CarouselComponent extends DestroyableBase implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  @Input() public vm: CarouselViewModel;
  @Input() public isInfinite = true;
  @Input() public preventHover = false;
  @Input() public carouselTitle = '';
  @Input() public whiteProgressBar = false;
  @Input() public replaceUrl = false;
  @Input() public showSeoTitle = false;
  @Output() public carouselItemSelect: EventEmitter<{
    carouselItemDetails: CarouselItemVideoDetailsForGtmAnalytics;
    carouselTitle: string;
  }> = new EventEmitter();
  @Output() public carouselItemImpression = new EventEmitter<{ guid: string; index: number; carouselItemType: string }>();
  @ViewChildren(IntersectionMarkerDirective) public intersectionItems: QueryList<IntersectionMarkerDirective>;

  public isMobile$: Observable<boolean>;
  public showPagerButtons = false;
  // slightly different behavior for init state, which changes after first next click
  // no left items / swipe button should shown
  public initState = true;
  public animating = false;
  public carouselItemsToShow: CarouselViewModelItem[] = [];
  public imageLoaded = false;
  public carouselType = CarouselType;
  public observer = new IntersectionObserver((entries) => this.intersect(entries), { rootMargin: `0px`, threshold: 1.0 });

  private translateX: number;
  private currentIndex = 0;
  private animationDirection: AnimationDirection;
  private transitionInProgress = false;
  private readonly measuredItemGuids: string[] = [];

  @HostListener('mouseenter', ['$event'])
  public onEnter(e: MouseEvent) {
    this.showPagerButtons = true;
  }

  @HostListener('mouseleave', ['$event'])
  public onLeave(e: MouseEvent) {
    this.showPagerButtons = false;
  }

  constructor(
    private readonly screenService: ScreenService,
    private readonly translateService: TranslateService,
    private readonly gatewayService: AbstractGatewayService,
    @Inject(PLATFORM_ID) private readonly platform,
    @Optional() private readonly detectDeviceService: AbstractDetectDeviceService
  ) {
    super();
    this.isMobile$ = combineLatest([this.screenService.isXS$, this.screenService.isSM$]).pipe(map(([isXS, isSM]) => isXS || isSM));
  }

  // ---------------------------------
  // lifecycle methods
  // ---------------------------------

  public ngOnInit(): void {
    this.initShowingCarouselItems();
    this.screenService.changed$.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.initShowingCarouselItems();
    });
    // reset carousel state on language change
    this.translateService.onLangChange.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.initShowingCarouselItems();
    });
  }

  public readonly preventHoverPreview$ = this.gatewayService.getCustomerStatus$().pipe(
    takeUntil(this.onDestroy$),
    map((customerStatus) => {
      return customerStatus === CustomerStatus.ANONYMOUS || this.preventHover;
    })
  );

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.vm && this.compareVmChanges(changes.vm)) {
      this.initShowingCarouselItems();
    }
  }

  private compareVmChanges(changes: SimpleChange): boolean {
    const { items: prevItems, resolved: prevResolved } = (changes.previousValue as CarouselViewModel) || {};
    const { items: currItems, resolved: currResolved } = (changes.currentValue as CarouselViewModel) || {};

    return (
      prevItems?.length !== currItems?.length || prevResolved !== currResolved || (prevResolved === undefined && currResolved === undefined)
    );
  }

  public ngAfterViewInit(): void {
    this.intersectionItems.changes.pipe(takeUntil(this.onDestroy$), startWith([])).subscribe(() => {
      this.observer.disconnect();
      this.intersectionItems.forEach((item) => this.observer.observe(item.element.nativeElement));
    });
  }

  public ngOnDestroy() {
    super.ngOnDestroy();
    this.observer.disconnect();
  }

  // ---------------------------------
  // public methods: used in template
  // ---------------------------------

  public get translateWithOffset() {
    // the "translate3d" css transform value is always calculated from the left side,
    // so we need to alter the calculation of it in case of rtl display
    return this.isArabic ? this.translateX - this.translateOffset : this.translateX + this.translateOffset;
  }

  public get aspectRatio(): string {
    const layout = CAROUSEL_ITEM_ASPECT_RATIO[this.vm.layout];
    return ASPECT_RATIO_CONFIG[layout];
  }

  public get displayedItemsLength(): number {
    /* eslint-disable @typescript-eslint/no-magic-numbers */
    if (CAROUSEL_ITEM_DISPLAY[this.vm.layout]) {
      // in case of server load, if we display more items that's not a problem,
      // but if we only show 3 items on a big screen while loading it doesn't look good
      // so we should use the Screen.XL count as a default
      return CAROUSEL_ITEM_DISPLAY[this.vm.layout][this.screenService.activeScreen || Screen.XL];
    } else {
      console.warn(`Unsupported layout: ${this.vm.layout}`);
      return 1;
    }
  }

  public get placeholderSize(): [number, number] {
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    return this.aspectRatio.split(':').map((n) => Number(n) * 10) as [number, number];
  }

  public get isCarouselScrollable(): boolean {
    return this.vm.items.length > CAROUSEL_ITEM_DISPLAY[this.vm.layout][this.screenService.activeScreen];
  }

  public get filteredCarouselItemtoShow() {
    return this.isCarouselScrollable ? this.carouselItemsToShow : this.vm.items;
  }

  public get showPlaceholder() {
    switch (this.vm.type) {
      case CarouselType.ContinueWatchingCarousel:
      case CarouselType.MyListCarousel:
        return !this.vm.resolved;
      case CarouselType.BasicCarousel:
      case CarouselType.CollectionCarousel:
      case CarouselType.GenericCarousel:
      case CarouselType.MickMack:
      case CarouselType.RecommendedCarousel:
      case CarouselType.TopCarousel:
      default:
        return this.filteredCarouselItemtoShow.length === 0;
    }
  }

  public get isArabic(): boolean {
    return this.translateService.currentLang === 'ar';
  }

  public get isLeftScrollPossible(): boolean {
    if (this.initState) {
      return this.isArabic;
    }

    return this.isInfinite || (this.isArabic ? this.nextIndex < this.vm.items.length : this.currentIndex > this.prevIndexWithoutInfinite);
  }

  public get isRightScrollPossible(): boolean {
    if (this.initState) {
      return !this.isArabic;
    }

    return this.isInfinite || (this.isArabic ? this.currentIndex > this.prevIndexWithoutInfinite : this.nextIndex < this.vm.items.length);
  }

  public get alignItemsCenter(): boolean {
    if (this.vm.type !== CarouselType.CollectionCarousel && this.vm.type !== CarouselType.SeasonCollectionCarousel) {
      return false;
    }
    if (isPlatformServer(this.platform)) {
      const screenSize = this.measureScreenSizeOnServer();
      return this.vm.items.length <= CAROUSEL_ITEM_DISPLAY[this.vm.layout][screenSize];
    } else return !this.isCarouselScrollable;
  }

  public onRightArrowClick() {
    this.scrollRightItems();
  }

  public onLeftArrowClick() {
    this.scrollLeftItems();
  }

  public onSwipeLeft() {
    if (this.isCarouselScrollable && this.isRightScrollPossible) {
      this.scrollRightItems();
    }
  }

  public onSwipeRight() {
    if (this.isCarouselScrollable && this.isLeftScrollPossible) {
      this.scrollLeftItems();
    }
  }

  public onCarouselTransitionEnd() {
    this.animating = false;

    this.updateCurrentIndex();
    if (!this.isInfinite) {
      if (this.initState) {
        this.initState = false;
      }
    } else if (this.initState) {
      this.updateInitialCarouselItemsToShow();
    } else {
      this.updateCarouselItemsToShow();
    }

    this.transitionInProgress = false;
  }

  public onCarouselItemSelect(carouselItemDetails: CarouselItemVideoDetailsForGtmAnalytics) {
    this.carouselItemSelect.emit({ carouselItemDetails, carouselTitle: this.carouselTitle });
  }

  public trackbyFn: TrackByFunction<CarouselViewModelItem> = (index, item) => item.guid;

  // ---------------------------------
  // private methods
  // ---------------------------------

  private intersect(entries: IntersectionObserverEntry[]) {
    entries.forEach(({ isIntersecting, target }) => {
      if (!isIntersecting) {
        return;
      }
      const entryGuid = target.getAttribute('guid');
      const carouselItemType = (this.vm.items.find((item: ContentViewModel) => item.guid === entryGuid) as ContentViewModel)?.type;
      if (this.measuredItemGuids.includes(entryGuid)) {
        return;
      }
      this.measuredItemGuids.push(entryGuid);
      const index = Number(target.getAttribute('index'));
      this.carouselItemImpression.emit({ guid: entryGuid, index, carouselItemType });
    });
  }

  private getNextLeftoverItems(): (ContentViewModel | GenericCarouselItemViewModel | SeasonCarouselItemViewModel)[] {
    // deleting the previous "prev items" from the list
    // 2 * this.displayedItemsLength because in the initial state we added both the prev items
    // and now we need to remove those and the former current items also

    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    return this.carouselItemsToShow.slice(2 * this.displayedItemsLength);
  }

  private getPrevLeftoverItems(): (ContentViewModel | GenericCarouselItemViewModel | SeasonCarouselItemViewModel)[] {
    // deleting the previous "next items" from the list

    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    return this.carouselItemsToShow.slice(0, this.displayedItemsLength + 2);
  }

  private updateCarouselItemsToShow(): void {
    let leftOverItems = [];

    switch (this.animationDirection) {
      case AnimationDirection.NEXT:
        leftOverItems = this.isArabic ? this.getPrevLeftoverItems() : this.getNextLeftoverItems();

        // reset translateX after animation
        this.translateX += BASE_TRANSLATE_X;
        break;
      case AnimationDirection.PREVIOUS:
        leftOverItems = this.isArabic ? this.getNextLeftoverItems() : this.getPrevLeftoverItems();

        // reset translateX after animation
        this.translateX -= BASE_TRANSLATE_X;
    }

    this.carouselItemsToShow = [...this.prevPageItems, ...leftOverItems, ...this.nextPageItems];
  }

  private updateInitialCarouselItemsToShow(): void {
    this.initState = false;
    this.carouselItemsToShow = [
      ...this.prevPageItems,
      // this.displayedItemsLength - 1 because we need to show that extra one item on the edge
      ...this.carouselItemsToShow.slice(this.displayedItemsLength - 1),
      ...this.nextPageItems,
    ];
    this.translateX = this.isArabic ? BASE_TRANSLATE_X : -BASE_TRANSLATE_X;
  }

  private updateCurrentIndex(): void {
    switch (this.animationDirection) {
      case AnimationDirection.NEXT:
        this.currentIndex = this.getNextIndexWithArabic();
        break;
      case AnimationDirection.PREVIOUS:
        this.currentIndex = this.getPrevIndexWithArabic();
        break;
    }
  }

  private getNextIndexWithArabic(): number {
    if (this.isInfinite) {
      return this.isArabic ? this.prevIndex : this.nextIndex;
    }
    return this.isArabic ? this.prevIndexWithoutInfinite : this.nextIndexWithoutInfinite;
  }

  private getPrevIndexWithArabic(): number {
    if (this.isInfinite) {
      return this.isArabic ? this.nextIndex : this.prevIndex;
    }
    return this.isArabic ? this.nextIndexWithoutInfinite : this.prevIndexWithoutInfinite;
  }

  private get nextPageEndIndex(): number {
    return this.nextIndex + this.displayedItemsLength;
  }

  private get nextIndexWithoutInfinite() {
    const itemsLength = this.vm.items.length;
    const leftOverItems = this.displayedItemsLength - (this.nextPageEndIndex - itemsLength);
    return this.nextPageEndIndex > itemsLength ? this.currentIndex + leftOverItems : this.nextIndex;
  }

  private get prevIndexWithoutInfinite() {
    return this.prevIndex < 0 ? 0 : this.prevIndex;
  }

  // eslint-disable-next-line complexity, sonarjs/cognitive-complexity
  private getScrollingTranslatePercentRatio(scrollDirection: 'left' | 'right') {
    if (this.isInfinite) {
      return 1;
    }

    const itemsLength = this.vm.items.length;
    const remainderItems = itemsLength % this.displayedItemsLength || this.displayedItemsLength;

    if ((scrollDirection === 'left' && !this.isArabic) || (scrollDirection === 'right' && this.isArabic)) {
      return this.prevIndex >= 0 ? 1 : remainderItems / this.displayedItemsLength;
    } else {
      // because we need the next page END index here instead of the start index, to check if we should scroll a whole page
      return this.nextPageEndIndex <= itemsLength ? 1 : remainderItems / this.displayedItemsLength;
    }
  }

  private scrollLeftItems() {
    if (this.transitionInProgress || (!this.isArabic && this.initState)) {
      return;
    }

    this.animating = true;
    this.translateX += this.getScrollingTranslatePercentRatio('left') * BASE_TRANSLATE_X;
    this.animationDirection = AnimationDirection.PREVIOUS;
    this.transitionInProgress = true;
  }

  private scrollRightItems() {
    if (this.transitionInProgress || (this.isArabic && this.initState)) {
      return;
    }

    this.animating = true;
    this.translateX -= this.getScrollingTranslatePercentRatio('right') * BASE_TRANSLATE_X;
    this.animationDirection = AnimationDirection.NEXT;
    this.transitionInProgress = true;
  }

  private initShowingCarouselItems() {
    this.currentIndex = 0;
    // TODO: setting carouselItemsToShow to this.vm.items will remove DOM lazy loading
    this.carouselItemsToShow = this.isInfinite ? [...this.initialPageItems, ...this.nextPageItems] : this.vm.items;
    this.translateX = 0;
    this.initState = true;
  }

  private get nextIndex(): number {
    const nextIndexIsNotInTurnover = this.currentIndex + this.displayedItemsLength < this.vm.items.length;
    return !this.isInfinite || nextIndexIsNotInTurnover
      ? this.currentIndex + this.displayedItemsLength
      : this.displayedItemsLength - (this.vm.items.length - this.currentIndex);
  }

  private get prevIndex(): number {
    const prevIndexIsNotInTurnover = this.currentIndex - this.displayedItemsLength >= 0;
    return !this.isInfinite || prevIndexIsNotInTurnover
      ? this.currentIndex - this.displayedItemsLength
      : this.vm.items.length - (this.displayedItemsLength - this.currentIndex);
  }

  private getItems(startIndex: number, length: number): CarouselViewModelItem[] {
    const { items } = this.vm;

    const startIndexRotated = startIndex < 0 ? this.vm.items.length + startIndex : startIndex;

    const insideArray = items.slice(startIndexRotated, startIndexRotated + length);
    const turnBackPart = items.slice(0, length - insideArray.length);
    return [...insideArray, ...turnBackPart];
  }

  private get prevPageItems(): CarouselViewModelItem[] {
    // start the prev item with one extra item on the beginning,
    // because of the extra "visible but not part of the active items" at the edge
    return this.getItems(this.prevIndex - 1, this.displayedItemsLength);
  }

  private get initialPageItems(): CarouselViewModelItem[] {
    // displayedItemsLength + 1 to show the extra item on the right (or left if arabic)
    return this.getItems(this.currentIndex, this.displayedItemsLength + 1);
  }

  private get nextPageItems(): CarouselViewModelItem[] {
    return this.getItems(this.nextIndex + 1, this.displayedItemsLength);
  }

  private get translateOffset(): number {
    if (this.initState || !this.isInfinite) {
      return 0;
    }
    return -1 * (BASE_TRANSLATE_X / this.displayedItemsLength);
  }

  private measureScreenSizeOnServer(): Screen {
    if (this.detectDeviceService.isMobilePhone) {
      return Screen.XS;
    } else if (this.detectDeviceService.isTierTablet) {
      return Screen.SM;
    } else return Screen.MD;
  }
}
